/**
 * Policy guard client for the AI Cortex.
 *
 * Provides client-side PII scanning and response-filter handling so
 * the ChatBot can warn users before sending sensitive content to the
 * backend and surface any server-side policy blocks.
 *
 * @module gibbs/cortex/policyGuard
 */

// ── PII patterns ─────────────────────────────────────────────────

/**
 * @typedef {Object} PIIPattern
 * @property {string} type - Pattern category
 * @property {RegExp} regex - Detection regex
 */

/** @type {PIIPattern[]} */
const PII_PATTERNS = [
    {
        type: 'ssn',
        regex: /\d{3}-\d{2}-\d{4}/g,
    },
    {
        type: 'email',
        regex: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    },
    {
        type: 'phone',
        // eslint-disable-next-line no-useless-escape
        regex: /(\+\d{1,3}[-.]?)?\(?\d{3}\)?[-.]?\d{3}[-.]?\d{4}/g,
    },
    {
        type: 'credit_card',
        regex: /\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}/g,
    },
];

// ── Scanning ──────────────────────────────────────────────────────

/**
 * @typedef {Object} PIIMatch
 * @property {string} type - PII category (ssn, email, phone, credit_card)
 * @property {string} match - The matched text
 * @property {number} position - Start index in the input
 */

/**
 * Scan text for PII patterns.
 *
 * @param {string} text - Text to scan
 * @returns {PIIMatch[]} Array of detected PII instances
 */
export function scanForPII(text) {
    /** @type {PIIMatch[]} */
    const matches = [];

    for (const { type, regex } of PII_PATTERNS) {
        // Reset lastIndex for global regexes
        regex.lastIndex = 0;
        let m = regex.exec(text);
        while (m) {
            matches.push({
                type,
                match: m[0],
                position: m.index,
            });
            m = regex.exec(text);
        }
    }

    return matches;
}

// ── Guard gate ────────────────────────────────────────────────────

/**
 * @typedef {Object} GuardResult
 * @property {boolean} safe - True if no PII detected
 * @property {PIIMatch[]} issues - Detected PII items
 */

/**
 * Check a message for PII before sending to the backend.
 *
 * The ChatBot should call this before dispatching; if `safe` is
 * false, warn the user and offer to redact before proceeding.
 *
 * @param {string} message - User message text
 * @returns {GuardResult}
 */
export function guardMessage(message) {
    const issues = scanForPII(message);
    return {
        safe: issues.length === 0,
        issues,
    };
}

// ── Response filter handling ──────────────────────────────────────

/**
 * @typedef {Object} GuardResponseResult
 * @property {boolean} filtered - True if the response was blocked
 * @property {string|null} reason - Reason provided by the policy guard
 * @property {string|null} rule - Rule that triggered the block
 */

/**
 * Check whether a backend response was filtered by the policy guard
 * middleware and extract the reason for display to the user.
 *
 * @param {Object} response - Parsed backend response body
 * @returns {GuardResponseResult}
 */
export function handleGuardResponse(response) {
    if (
        response
        && response.policy_blocked
    ) {
        return {
            filtered: true,
            reason: response.policy_reason || 'Content blocked by policy.',
            rule: response.policy_rule || null,
        };
    }
    return { filtered: false, reason: null, rule: null };
}
